/**
 * Turn a string that is about to be rendered as HTML into one that can
 * only be formatting.
 *
 * Three places render server-held strings with dangerouslySetInnerHTML:
 * case chat messages, the discussion list on the case page, and the
 * orthodontist/technician notes. All three carry markup on purpose —
 * the system notices bold the case number, and the notes come out of the
 * rich-text editor — so they cannot simply be printed as text. But the
 * chat message is typed by a person, which meant `<img src=x
 * onerror=...>` in a message ran in the reader's session, and the reader
 * is usually an admin.
 *
 * So: parse the string, keep a short list of formatting tags, drop
 * everything else (keeping the text inside it), and strip every
 * attribute except a plain http/https/mailto href. Anything that could
 * execute — script, style, iframe, on* handlers, javascript: URLs — has
 * nowhere left to live.
 */

const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'BR', 'P', 'DIV', 'SPAN',
  'UL', 'OL', 'LI', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'PRE', 'CODE', 'A',
]);

/** Tags whose contents go too — an unrendered <script> body is not text. */
const DROP_ENTIRELY = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'SVG', 'MATH']);

const SAFE_HREF = /^(https?:|mailto:)/i;

const clean = (node) => {
  // childNodes is live and we remove as we go, so walk a copy.
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) return;

    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove();
      return;
    }

    if (DROP_ENTIRELY.has(child.tagName)) {
      child.remove();
      return;
    }

    clean(child);

    if (!ALLOWED_TAGS.has(child.tagName)) {
      // Unwrap rather than delete: the words someone wrote inside an
      // unexpected tag are still their message.
      child.replaceWith(...Array.from(child.childNodes));
      return;
    }

    Array.from(child.attributes).forEach((attr) => {
      const keep = child.tagName === 'A'
        && attr.name.toLowerCase() === 'href'
        && SAFE_HREF.test(attr.value.trim());

      if (!keep) child.removeAttribute(attr.name);
    });

    if (child.tagName === 'A') {
      child.setAttribute('rel', 'noopener noreferrer');
      child.setAttribute('target', '_blank');
    }
  });
};

export const sanitizeHtml = (html) => {
  if (html === null || html === undefined || html === '') return '';

  try {
    // Parsed in an inert document: nothing loads and nothing runs while
    // this is being cleaned up.
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    clean(doc.body);
    return doc.body.innerHTML;
  } catch (e) {
    // If parsing ever fails, show the text and no markup at all.
    return String(html).replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
};

/** What dangerouslySetInnerHTML wants, already made safe. */
export const safeHtml = (html) => ({ __html: sanitizeHtml(html) });

export default safeHtml;
